/**
 * One face per CSS address, for every surface that loads brand fonts.
 *
 * A browser holding two faces at the same family, weight and style renders
 * the one registered last, so the face an ad gets depends on load order or
 * list order. The editor, the canvas and the export renderer must make the
 * same choice, so they all decide it here. Browser-safe: `packages/app`
 * deep-imports this module.
 */

import {
  type RetirableFont,
  isActiveFont,
  isRetiredFont,
} from "./font-retirement";

const NAMED_WEIGHTS: Record<string, string> = { normal: "400", bold: "700" };

/** What a face answers to in CSS: `font-family`, `font-weight` and `font-style`. */
export function fontFaceAddress(
  family: string,
  weight = "normal",
  style = "normal",
): string {
  return `${family.trim().toLowerCase()}\n${NAMED_WEIGHTS[weight] ?? weight}\n${style}`;
}

/** A face that registers under its typographic family when it has one, and under its own name. */
export type AddressableFontFace = {
  name: string;
  family?: string;
  weight?: string;
  style?: string;
};

/** How a face may register without sharing an address with an earlier face. */
export type FontFaceClaim = "family-and-name" | "name-only" | "none";

/**
 * Decides how each face registers so that no two faces share an address. Earlier faces win.
 * Families claim before names, so a file named like a family never takes that address from the family's face.
 */
export function claimFontFaceAddresses<Face extends AddressableFontFace>(
  faces: readonly Face[],
): { face: Face; claim: FontFaceClaim }[] {
  const claimed = new Set<string>();
  const claim = (address: string) => {
    if (claimed.has(address)) return false;
    claimed.add(address);
    return true;
  };
  const afterFamilies = faces.map((face) => ({
    face,
    keepsFamily:
      face.family !== undefined &&
      claim(fontFaceAddress(face.family, face.weight, face.style)),
  }));
  return afterFamilies.map(
    ({ face, keepsFamily }): { face: Face; claim: FontFaceClaim } => {
      const ownsName = claim(
        fontFaceAddress(face.name, face.weight, face.style),
      );
      if (keepsFamily) return { face, claim: "family-and-name" };
      return { face, claim: ownsName ? "name-only" : "none" };
    },
  );
}

/** Live fonts before retired ones, each in its own order, so a retired face never takes an address a live face answers. */
export function liveFontsFirst<Font extends RetirableFont>(
  fonts: readonly Font[],
): Font[] {
  return [
    ...fonts.filter((font) => isActiveFont(font)),
    ...fonts.filter((font) => isRetiredFont(font)),
  ];
}
